/**
 * Settings > Billing. Cards render top to bottom in this order; add new
 * billing admin cards here (CLAUDE.md §3.6).
 */
import React from 'react';
import { View } from 'react-native';
import { Text } from '@/components/ui/text';
import CancellationInsightsCard from '../../../../components/admin/CancellationInsightsCard';
import SubscriptionCleanupCard from '../../../../components/admin/SubscriptionCleanupCard';
import StripePriceCacheCard from '../../../../components/admin/StripePriceCacheCard';
import StripeWebhookInboxCard from '../../../../components/admin/StripeWebhookInboxCard';
import CreditAdjustCard from '../../../../components/admin/CreditAdjustCard';
import { useTranslation } from '../../../../hooks/useTranslation';
import SchoolBillingList from './SchoolBillingList';
import type { School } from '../platformAdminTypes';
import type { SchoolActions } from '../hooks/useSchoolActions';

interface BillingSettingsSectionProps {
  schools: School[];
  focusedSchoolId: number | null;
  schoolActions: SchoolActions;
  onCreateSchool: () => void;
  /** Reloads platform data after a real subscription cleanup. */
  onPlatformDataChanged: () => void;
}

export default function BillingSettingsSection({
  schools,
  focusedSchoolId,
  schoolActions,
  onCreateSchool,
  onPlatformDataChanged,
}: BillingSettingsSectionProps) {
  const { t } = useTranslation();

  return (
    <>
      <CancellationInsightsCard />

      {/* Subscription Cleanup */}
      <SubscriptionCleanupCard onCleaned={onPlatformDataChanged} />

      {/* Stripe Price Cache */}
      <StripePriceCacheCard />

      {/* Manual credit adjustment for a user or school */}
      <CreditAdjustCard />

      <View className="gap-2">
        <Text className="text-xl font-bold text-foreground">
          {t('platformAdmin.billingSettingsTitle')}
        </Text>
        <Text className="text-sm text-muted-foreground">
          {t('platformAdmin.billingSettingsDescription')}
        </Text>
      </View>

      {/* Stripe webhook inbox: list + retry + reconcile (BILL-17) */}
      <StripeWebhookInboxCard />

      <SchoolBillingList
        schools={schools}
        focusedSchoolId={focusedSchoolId}
        schoolActions={schoolActions}
        onCreateSchool={onCreateSchool}
      />
    </>
  );
}
